/**
 * The Job analysis tab's picker and address, without React. RidgeWill/org-admin#111.
 *
 * Pure and exported for tests, like fleet-runs.ts: searching and sorting the job
 * index fleet-api sends, and turning a job into the hash address that opens the
 * tab on it, and back.
 */
import type { JobIndexEntry, JobKey } from './fleet-api.js';

export type JobSort = 'name' | 'repo' | 'runs' | 'median' | 'last';
export type SortDirection = 'asc' | 'desc';

/** What a column sorts by first, before it is clicked again: names A to Z, numbers and dates biggest first. */
export const FIRST_DIRECTION: Record<JobSort, SortDirection> = {
  name: 'asc',
  repo: 'asc',
  runs: 'desc',
  median: 'desc',
  last: 'desc',
};

const jobName = (j: JobKey) => `${j.workflow} · ${j.job_name}`;

/**
 * Jobs matching every word of the search, in repo, workflow or job name, ignoring case.
 *
 * Every word rather than the whole string, so "auth test" finds auth's `test`
 * job without the reader knowing it is written "auth · test · test".
 */
export function searchJobs(jobs: JobIndexEntry[], query: string): JobIndexEntry[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return jobs;
  return jobs.filter((j) => {
    const haystack = `${j.repo} ${j.workflow} ${j.job_name}`.toLowerCase();
    return words.every((w) => haystack.includes(w));
  });
}

/**
 * Sorted by one column. A job with no median (it never passed) sorts last in
 * both directions: "no run time" is not "fastest".
 */
export function sortJobs(jobs: JobIndexEntry[], key: JobSort, direction: SortDirection): JobIndexEntry[] {
  const sign = direction === 'asc' ? 1 : -1;
  const byName = (a: JobIndexEntry, b: JobIndexEntry) => jobName(a).localeCompare(jobName(b)) || a.repo.localeCompare(b.repo);
  return [...jobs].sort((a, b) => {
    switch (key) {
      case 'name':
        return sign * byName(a, b);
      case 'repo':
        return sign * (a.repo.localeCompare(b.repo) || byName(a, b));
      case 'runs':
        return sign * (a.total - b.total) || byName(a, b);
      case 'median':
        if (a.median_s === null || b.median_s === null) {
          return a.median_s === b.median_s ? byName(a, b) : a.median_s === null ? 1 : -1;
        }
        return sign * (a.median_s - b.median_s) || byName(a, b);
      case 'last':
        return sign * a.last_run_at.localeCompare(b.last_run_at) || byName(a, b);
    }
  });
}

export const sameJob = (a: JobKey | null | undefined, b: JobKey | null | undefined): boolean =>
  !!a && !!b && a.repo === b.repo && a.workflow === b.workflow && a.job_name === b.job_name;

/** The hash address of the Job analysis tab open on one job, for `navigate` and for links. */
export function jobPath(job: JobKey): string {
  return `/ci-fleet/jobs?${new URLSearchParams({ repo: job.repo, workflow: job.workflow, job: job.job_name })}`;
}

/** The job a Job analysis address names: all three parts, or none, as fleet-api's `jobFrom` reads them. */
export function jobFromQuery(query: Record<string, string>): JobKey | undefined {
  const { repo, workflow, job } = query;
  return repo && workflow && job ? { repo, workflow, job_name: job } : undefined;
}
